import { useCallback, useEffect, useRef, useState } from "react";
import { LiveFeedEventSubKind, } from "../../apollo/graphql.generated";
import { useMqtt } from "../../features/websocket/WebsocketMqttConnection";
import { HLServiceEventType, } from "../../features/websocket/events";
import useEventSubscription from "../../features/websocket/useEventSubscription";
import { apolloClient } from "../../services/apolloClient";
import { GetLiveFeedDocument, } from "./live.graphql.generated";
const mapMessageToLiveEvent = (message) => {
    const mappedMessage = { ...message };
    return mappedMessage;
};
export default function useLiveEvents(options = {}) {
    var _a, _b, _c, _d, _e, _f;
    const { isWebsocketPaused = false, maxCount = 100, mainnetOnly = true, } = options;
    const { client } = useMqtt();
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [eventData, setEventData] = useState({ queued: [], data: null });
    const [initialData, setInitialData] = useState([]);
    const _isInitialDataLoaded = useRef(false);
    const _isLoading = useRef(false);
    const _isWebsocketPaused = useRef(false);
    _isWebsocketPaused.current = isWebsocketPaused;
    const count = (_b = (_a = eventData.data) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    const queuedCount = (_d = (_c = eventData.queued) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
    const canLoadMore = ((_f = (_e = eventData.data) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0) < maxCount;
    const subscribeToLiveFeed = useCallback(() => {
        client === null || client === void 0 ? void 0 : client.subscribe(HLServiceEventType.HighlightLiveFeed, (error) => {
            setSubscriptionError(error);
        });
        return () => {
            client === null || client === void 0 ? void 0 : client.unsubscribe(HLServiceEventType.HighlightLiveFeed);
        };
    }, [client]);
    useEffect(() => {
        return subscribeToLiveFeed();
    }, [subscribeToLiveFeed]);
    useEventSubscription(HLServiceEventType.HighlightLiveFeed, (message) => {
        try {
            if (_isInitialDataLoaded.current === false) {
                return;
            }
            const originalMessage = JSON.parse(message.payload);
            const transformedMessage = mapMessageToLiveEvent(originalMessage);
            if (_isWebsocketPaused.current === true) {
                setEventData((prevData) => {
                    return {
                        ...prevData,
                        queued: [...prevData.queued, transformedMessage],
                    };
                });
                return;
            }
            setEventData((prevData) => {
                var _a;
                const reversedQueuedData = prevData.queued.reverse();
                return {
                    queued: [],
                    data: [
                        transformedMessage,
                        ...reversedQueuedData,
                        ...((_a = prevData.data) !== null && _a !== void 0 ? _a : []),
                    ].slice(0, maxCount),
                };
            });
        }
        catch (error) {
            console.error("Error parsing message", message, error);
        }
    });
    const mergeQueuedEvents = useCallback(() => {
        setEventData((prevData) => {
            var _a;
            const reversedQueuedData = prevData.queued.reverse();
            return {
                queued: [],
                data: [...reversedQueuedData, ...((_a = prevData.data) !== null && _a !== void 0 ? _a : [])].slice(0, maxCount),
            };
        });
    }, [maxCount]);
    const loadInitialEvents = useCallback(async (limit = maxCount, collectionId) => {
        if (_isLoading.current === true)
            return;
        if (_isInitialDataLoaded.current === true)
            return;
        if (eventData.data != null)
            return;
        _isLoading.current = true;
        const data = await fetchLiveEvents(limit, mainnetOnly, collectionId);
        const newEvents = [];
        data.forEach((item) => {
            newEvents.push(mapMessageToLiveEvent(item));
        });
        setEventData((prevData) => {
            var _a;
            return { ...prevData, data: [...newEvents, ...((_a = prevData.data) !== null && _a !== void 0 ? _a : [])] };
        });
        setInitialData(newEvents);
        _isInitialDataLoaded.current = true;
        _isLoading.current = false;
    }, [eventData, maxCount, mainnetOnly]);
    const testQuery = useCallback(async () => {
        const data = await fetchLiveEvents();
        console.log("fetchLiveFeed", data);
    }, []);
    const testEmitEvent = useCallback(() => {
        if (client) {
            client.publish(HLServiceEventType.HighlightLiveFeed, JSON.stringify(createMockEvent()));
        }
    }, [client]);
    return {
        client,
        data: eventData.data,
        queued: eventData.queued,
        count,
        queuedCount,
        canLoadMore,
        isLoading: _isLoading.current,
        isInitialDataLoaded: _isInitialDataLoaded.current,
        initialData,
        subscriptionError,
        subscribeToLiveFeed,
        loadInitialEvents,
        mergeQueuedEvents,
        testQuery,
        testEmitEvent,
    };
}
const fetchLiveEvents = async (limit = 100, mainnetOnly = true, collectionId) => {
    const variables = { limit: limit, page: 1, mainnetOnly, collectionId };
    const { data } = await apolloClient.query({
        query: GetLiveFeedDocument,
        variables,
    });
    return data.getLiveFeed;
};
/**
 * MOCK DATA
 */
const createMockEvent = (id = new Date().toISOString(), date = Date.now()) => {
    console.log("createMockEvent", { id, date });
    const message = MOCK_LIVE_EVENT_WEBSOCKET_MESSAGE.message;
    const subKinds = Object.values(LiveFeedEventSubKind);
    const mockEvent = {
        ...message,
        kind: message.kind,
        subKind: subKinds[Math.floor(Math.random() * subKinds.length)],
        // collection: {
        //   ...message.collection,
        //   name: new Array(Math.ceil(Math.random() * 12) + 1)
        //     .fill("Collection")
        //     .join(" "),
        //   description: new Array(Math.ceil(Math.random() * 12) + 1)
        //     .fill("Token")
        //     .join(" "),
        // },
        createdAt: date,
    };
    if (mockEvent.mint != null) {
        mockEvent.mint = {
            ...mockEvent.mint,
            tokenIds: new Array(Math.ceil(Math.random() * 12) + 1)
                .fill(1)
                .map((_, i) => "" + i),
            value: (Math.random() * 10).toFixed(0),
        };
    }
    return mockEvent;
};
const MOCK_LIVE_EVENT_WEBSOCKET_MESSAGE = {
    topic: "highlight-live-feed",
    message: {
        blockHash: "0x5b9202745503ada704d8a81b3039293b05031f3f8198ce9fd967e05e37183754",
        collectionAddress: "0x14387437B7436b0d8f6Dd5A6D293bfd04Bf3Fcb8",
        logIndex: 46,
        eventLocation: "84532/2024-07-11/313ad556-0e95-e856-6bbc-38de87d08834/highlightxyz-mint-manager",
        kind: "highlightxyz",
        editionId: "0",
        subKind: "highlightxyz-mint-manager-num-token-mint",
        __edb_e__: "transaction",
        transactionIndex: 8,
        collection: {
            collectionType: "OpenEdition",
            collectionAddress: "0x14387437B7436b0d8f6Dd5A6D293bfd04Bf3Fcb8",
            size: 0,
            chainId: 84532,
            editionId: "0",
            name: "ASASDASD",
            description: "ASDASDSD",
            logo: "https://highlight-creator-assets.highlightstaging.xyz/main/image/a05cee7a-950b-4eb8-abe1-0a27fd57f213.png",
            id: "84532:0x14387437B7436b0d8f6Dd5A6D293bfd04Bf3Fcb8:0",
        },
        source: "highlightxyz-mint-manager",
        transactionHash: "0x2db4b2d54f2f1f658769a6a6940cb6244639b179528c457ca21c1ea0901574a8",
        mint: {
            tokenIds: ["1"],
            minter: "0xf38572c8375dbf08f137bb7f32270c3e21ed5d89",
            nativePayment: {
                saleContractAddress: "0x41cbab1028984a34c1338f437c726de791695ae8",
                paymentRecipient: "0xf38572C8375DBf08f137BB7F32270c3e21ED5D89",
                vectorId: "0x00000000000000000000000000000000000000000000000000000000000001d4",
                amountToCreator: "1000000000000000",
                percentageBPSOfTotal: 10000,
            },
        },
        createdAt: 1720699350322,
        chainId: 84532,
        blockNumber: 12465498,
        sk: "$transaction_1#logindex_46",
        __edb_v__: "1",
        from: "0xf38572c8375dbf08f137bb7f32270c3e21ed5d89",
        pk: "$evm#chainid_84532#transactionhash_0x2db4b2d54f2f1f658769a6a6940cb6244639b179528c457ca21c1ea0901574a8",
        to: "0x41cbab1028984a34c1338f437c726de791695ae8",
        status: "Mined",
        updatedAt: 1720699350322,
    },
};
